import React from 'react';
import {
    Box,
    Card,
    CardHeader,
    CardActions,
    makeStyles,
    IconButton,
    Collapse,
    Typography,
    Button,
    CardContent,
    Grid,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Skeleton } from '@material-ui/lab';
import transparentImg from '../../img/transparent.png';
import clsx from 'clsx';
import Garment from '../../objects/garments/garment';
import { useTranslation } from 'react-i18next';

interface Props {
    item: Garment;
    index: number;
    handleOnSubmit: ((item: Garment) => void) | null;
    currentGarmentId: number;
}

const useStyles = makeStyles((theme) => ({
    skeletonImg: {
        opacity: 0.1,
        backgroundImage: `url(${transparentImg})`,
    },
    root: {
        width: 200,
    },
    centered: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    media: {
        maxWidth: 200,
    },
    hidden: {
        display: 'none',
    },
    currentGarment: {
        border: '1px solid black',
    },
    cardHeader: {
        fontSize: '1rem',
        lineHeight: '1rem',
        padding: '7px 10px',
        minHeight: '4rem',
        borderBottom: '1px solid lightgrey',
    },
    expand: {
        transform: 'rotate(0deg)',
        marginLeft: 'auto',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
    priceLow: {
        fontWeight: 'bold',
        // color: '#f2434a',
    },
    priceSingle: {
        fontSize: '0.8rem',
    },
    noPadding: {
        padding: '0',
    },
}));

const ListItem: React.FC<Props> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const width = 200;
    const height = 200;

    const { item, index, handleOnSubmit, currentGarmentId } = props;
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [expanded, setExpanded] = React.useState(false);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    return (
        <Box key={index} my={1}>
            {item ? (
                <>
                    <Card
                        raised={false}
                        className={clsx(
                            classes.root,
                            item.getIdGarment() === currentGarmentId &&
                                classes.currentGarment
                        )}
                    >
                        <CardHeader
                            className={classes.cardHeader}
                            title={item.getName()}
                            disableTypography={true}
                        />

                        <CardContent className={classes.noPadding}>
                            <Skeleton
                                className={clsx(
                                    classes.skeletonImg,
                                    loaded && classes.hidden
                                )}
                                variant="rect"
                                width={width}
                                height={height}
                            />
                            <img
                                src={item.getImage()}
                                title={item.getName()}
                                alt={item.getName()}
                                className={clsx(
                                    classes.media,
                                    !loaded && classes.hidden
                                )}
                                onLoad={() => {
                                    setLoaded(true);
                                }}
                            />
                        </CardContent>
                        <CardActions
                            disableSpacing
                            className={classes.centered}
                        >
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item>
                                    <IconButton
                                        className={clsx(classes.expand, {
                                            [classes.expandOpen]: expanded,
                                        })}
                                        size="small"
                                        onClick={handleExpandClick}
                                        aria-expanded={expanded}
                                        aria-label="show more"
                                        title={
                                            expanded
                                                ? t('Hide more information')
                                                : t('Show more information')
                                        }
                                    >
                                        <ExpandMoreIcon />
                                    </IconButton>
                                </Grid>
                                <Grid item>
                                    {currentGarmentId !==
                                        item.getIdGarment() && (
                                        <Button
                                            onClick={() => {
                                                if (handleOnSubmit !== null) {
                                                    handleOnSubmit(item);
                                                }
                                            }}
                                        >
                                            {t('Change')}
                                        </Button>
                                    )}
                                </Grid>
                            </Grid>
                        </CardActions>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <CardContent>
                                <div
                                    className="content"
                                    dangerouslySetInnerHTML={{
                                        __html: item.getDescriptionShort(),
                                    }}
                                ></div>
                                <Typography className={classes.priceLow}>
                                    {t('From:')} {item.getPriceLow()}
                                </Typography>
                                <Typography className={classes.priceSingle}>
                                    {t('Single price:')} {item.getPrice()}
                                </Typography>
                                <br />
                                <Typography
                                    className={classes.priceSingle}
                                    title={t(
                                        'Number of garment parts compatible between your garment and this one.'
                                    )}
                                >
                                    {t('Compatibility:')} <br />
                                    <strong>
                                        {item.getSameGarmentPartCount()}/
                                        {item.getGarmentPartCount()}
                                    </strong>
                                    {t(' garment parts share the same layout')}
                                </Typography>
                            </CardContent>
                        </Collapse>
                    </Card>
                </>
            ) : (
                <Skeleton
                    className={classes.skeletonImg}
                    variant="rect"
                    width={width}
                    height={height}
                />
            )}
        </Box>
    );
};

export default ListItem;
